import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import apiService from '../../services/api';
import VoicePreviewButton from './VoicePreviewButton';

const TIER_LABELS = {
  1: { label: 'Recommended', color: 'success' },
  2: { label: 'Professional', color: 'primary' },
  3: { label: 'Selective', color: 'warning' },
  4: { label: 'Specialist', color: 'default' },
};

const GENDER_LABELS = {
  female: 'Female',
  male:   'Male',
};

/**
 * VoiceSelector — Rich voice selection grid with preview, filtering, and tier badges.
 * Used in Admin platform settings, Therapist voice settings, and Persona management.
 */
const VoiceSelector = ({
  value,
  onChange,
  token,
  showPlatformDefault = false,
  compact = false,
  accentPresetId = null,
  /** Optional voice id that "Use platform default" resolves to. When provided
   *  the platform-default tile shows the name + gender so authors don't get
   *  surprised by a male fallback when the persona is female. */
  platformDefaultVoiceId = null,
}) => {
  const [voices, setVoices] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [genderFilter, setGenderFilter] = useState('all');

  useEffect(() => {
    const fetchVoices = async () => {
      try {
        const data = await apiService.getVoiceCatalogue(token);
        setVoices(data.voices || []);
      } catch (err) {
        console.error('Failed to fetch voice catalogue:', err);
      }
    };
    if (token) fetchVoices();
  }, [token]);

  const filteredVoices = voices.filter(v => {
    if (filter !== 'all' && v.tier !== parseInt(filter)) return false;
    if (genderFilter !== 'all' && v.gender !== genderFilter) return false;
    if (search && !v.id.toLowerCase().includes(search.toLowerCase()) &&
        !v.characteristic.toLowerCase().includes(search.toLowerCase()) &&
        !v.description.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const resolvedDefault = platformDefaultVoiceId
    ? voices.find(v => v.id === platformDefaultVoiceId)
    : null;

  if (compact) {
    // Compact mode — simple dropdown with preview
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Voice</InputLabel>
          <Select
            value={value || ''}
            onChange={(e) => onChange(e.target.value || null)}
            label="Voice"
          >
            {showPlatformDefault && <MenuItem value="">Use platform default</MenuItem>}
            {voices.map(v => (
              <MenuItem key={v.id} value={v.id}>
                {v.id} — {v.characteristic}
                {v.tier === 1 ? ' ★' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {value && <VoicePreviewButton voiceName={value} token={token} accentPresetId={accentPresetId} />}
      </Box>
    );
  }

  // Full grid mode
  return (
    <Box>
      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search voices..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Filter by tier</InputLabel>
          <Select value={filter} onChange={(e) => setFilter(e.target.value)} label="Filter by tier">
            <MenuItem value="all">All voices ({voices.length})</MenuItem>
            <MenuItem value="1">Recommended ({voices.filter(v => v.tier === 1).length})</MenuItem>
            <MenuItem value="2">Professional ({voices.filter(v => v.tier === 2).length})</MenuItem>
            <MenuItem value="3">Selective ({voices.filter(v => v.tier === 3).length})</MenuItem>
            <MenuItem value="4">Specialist ({voices.filter(v => v.tier === 4).length})</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Gender</InputLabel>
          <Select value={genderFilter} onChange={(e) => setGenderFilter(e.target.value)} label="Gender">
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="female">Female ({voices.filter(v => v.gender === 'female').length})</MenuItem>
            <MenuItem value="male">Male ({voices.filter(v => v.gender === 'male').length})</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {showPlatformDefault && (
        <Card
          variant="outlined"
          sx={{
            mb: 2,
            cursor: 'pointer',
            border: !value ? '2px solid' : '1px solid',
            borderColor: !value ? 'primary.main' : 'divider',
            bgcolor: !value ? 'primary.50' : 'transparent',
          }}
          onClick={() => onChange(null)}
        >
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" fontWeight={!value ? 'bold' : 'normal'}>
                  Use platform default
                </Typography>
                {resolvedDefault && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      — {resolvedDefault.id}
                    </Typography>
                    {resolvedDefault.gender && resolvedDefault.gender !== 'unknown' && (
                      <Chip
                        size="small"
                        label={GENDER_LABELS[resolvedDefault.gender]}
                        color={resolvedDefault.gender === 'female' ? 'secondary' : 'default'}
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: 20 }}
                      />
                    )}
                    <Chip
                      size="small"
                      label={resolvedDefault.characteristic}
                      variant="outlined"
                      sx={{ fontSize: '0.65rem', height: 20 }}
                    />
                  </>
                )}
                {platformDefaultVoiceId && !resolvedDefault && (
                  <Typography variant="body2" color="text.secondary">
                    — {platformDefaultVoiceId}
                  </Typography>
                )}
              </Box>
              {!value && <CheckCircleIcon color="primary" fontSize="small" />}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Voice grid */}
      <Grid container spacing={1.5}>
        {filteredVoices.map(voice => {
          const isSelected = value === voice.id;
          const tierInfo = TIER_LABELS[voice.tier];

          return (
            <Grid item xs={12} sm={6} md={4} key={voice.id}>
              <Card
                variant="outlined"
                sx={{
                  cursor: 'pointer',
                  border: isSelected ? '2px solid' : '1px solid',
                  borderColor: isSelected ? 'secondary.main' : 'divider',
                  bgcolor: isSelected ? 'secondary.50' : 'transparent',
                  '&:hover': { borderColor: 'secondary.light', bgcolor: 'action.hover' },
                  transition: 'all 0.15s',
                }}
                onClick={() => onChange(voice.id)}
              >
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle2" fontWeight="bold">{voice.id}</Typography>
                      {voice.gender && voice.gender !== 'unknown' && (
                        <Chip
                          label={GENDER_LABELS[voice.gender]}
                          size="small"
                          color={voice.gender === 'female' ? 'secondary' : 'default'}
                          variant="outlined"
                          sx={{ fontSize: '0.65rem', height: 20 }}
                        />
                      )}
                      <Chip label={voice.characteristic} size="small" variant="outlined" />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <VoicePreviewButton voiceName={voice.id} token={token} size="small" accentPresetId={accentPresetId} />
                      {isSelected && <CheckCircleIcon color="secondary" fontSize="small" />}
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {voice.description}
                  </Typography>
                  <Chip label={tierInfo.label} size="small" color={tierInfo.color} sx={{ mr: 0.5 }} />
                  {voice.tags.slice(0, 3).map(tag => (
                    <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ mr: 0.5, fontSize: '0.65rem' }} />
                  ))}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {filteredVoices.length === 0 && (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
          No voices match your search criteria
        </Typography>
      )}
    </Box>
  );
};

export default VoiceSelector;
